import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../stores/auth.store';
import { toast } from 'react-hot-toast';
import { FullScreenLoader } from '@/features/ui/components/FullScreenLoader';
import { logger } from '@/features/help/utils/logger';
import api from '@/features/api/axios';
import { useSubscriptionStore } from '../stores/subscription.store';

export const CheckoutSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const hasHandledCallback = useRef(false);
  const { isLoading: isAuthLoading, isAuthenticated } = useAuthStore();
  const { checkSubscription } = useSubscriptionStore();

  useEffect(() => {
    const handleCheckoutSuccess = async () => {
      logger.debug('CheckoutSuccess', 'Effect triggered', {
        isAuthLoading,
        isAuthenticated,
        hasHandled: hasHandledCallback.current,
      });

      if (isAuthLoading) {
        logger.debug('CheckoutSuccess', 'Auth still loading, waiting...');
        return;
      }

      if (!isAuthenticated) {
        logger.error(
          'CheckoutSuccess',
          'User not authenticated after auth check. Redirecting to login.'
        );
        toast.error('Authentication session issue. Please log in again.');
        navigate('/login');
        return;
      }

      if (hasHandledCallback.current) {
        logger.debug('CheckoutSuccess', 'Callback already handled, skipping.');
        return;
      }
      hasHandledCallback.current = true;
      logger.debug('CheckoutSuccess', 'Proceeding to handle checkout success.');

      const sessionId = searchParams.get('session_id');

      if (!sessionId) {
        logger.error('CheckoutSuccess', 'No session_id found in URL.');
        toast.error('Payment verification failed.');
        navigate('/dashboard'); // Navigate somewhere safe
        return;
      }

      logger.info('CheckoutSuccess', 'Checkout session ID received, processing...');
      try {
        const response = await api.get(
          `/api/subscription/checkout-success?session_id=${sessionId}`
        );
        if (response.data.success) {
          await checkSubscription();
          toast.success('Subscription activated successfully!');
        }
        logger.info('CheckoutSuccess', 'Subscription activated. Navigating to dashboard.');
        navigate('/dashboard');
      } catch (error) {
        logger.error('CheckoutSuccess', 'Error during checkout success processing', { error });
        toast.error('Failed to activate subscription. Please contact support.');
        navigate('/dashboard'); // Navigate somewhere safe
      }
    };

    if (!isAuthLoading) {
      handleCheckoutSuccess();
    }
  }, [searchParams, navigate, checkSubscription, isAuthLoading, isAuthenticated]);

  return <FullScreenLoader message="Finalizing your subscription..." showOverlay={false} />;
};
